import { FC, ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useMobileSidebar } from '@/contexts/MobileSidebar/MobileSidebarContext';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import useSidebarPinned from '@/hooks/useSidebarPinned';
import ManagementAreaSidebar from './ManagementAreaSidebar';

const ManagementAreaProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const { currentUser, isCurrentUserAdmin, isCurrentUserDebugger } = useAuthContext();
  const { isSidebarDocked } = useDeviceDetection();
  const [isPinned, togglePinned] = useSidebarPinned();
  const { isMobileSidebarOpen, closeMobileSidebar } = useMobileSidebar();
  const { pathname } = useLocation();

  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = pathname;
      if (!isSidebarDocked && isMobileSidebarOpen) {
        closeMobileSidebar();
      }
    }
  }, [pathname, isSidebarDocked, isMobileSidebarOpen, closeMobileSidebar]);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (!isCurrentUserAdmin() && !isCurrentUserDebugger()) {
    return <Navigate to="/" />;
  }

  const isExpanded = isSidebarDocked || isMobileSidebarOpen;
  const showOverlay = !isSidebarDocked && isMobileSidebarOpen;
  const isRail = isSidebarDocked && !isPinned;

  return (
    <>
      <div className={`sidebar-container ${isExpanded ? 'sidebar-open' : ''} ${isRail ? 'sidebar-rail' : ''}`}>
        <ManagementAreaSidebar
          isExpanded={isExpanded}
          isPinned={isPinned}
          isRail={isRail}
          onTogglePinned={togglePinned}
        />
      </div>
      {showOverlay && (
        <div className="sidebar-overlay" onClick={closeMobileSidebar} aria-hidden="true" />
      )}
      <div className="content-container">
        {children}
      </div>
    </>
  );
};

export default ManagementAreaProvider;
